<script setup>
import FileApi from "@api/file.api";
import { shallowRef, ref, watch, computed } from "vue";
import { ElNotification } from "element-plus";

//==============================================================================
const uploadRef = ref(null);
const fileList = shallowRef([]);
const props = defineProps({
    model: {
        type: String,
        required: false,
    },
    name: {
        // input name
        type: String,
    },
    type: {
        type: String,
        required: true,
    },
    multiple: {
        type: Boolean,
        default: false,
    },
    limit: {
        type: Number,
        default: 1,
    },
    allowedFiles: {
        type: String,
        default: ".jpg , .png , .svg",
    },
});
const emit = defineEmits([
    "successUpload",
    "errorUpload", 
    "successDeleteUpload",
    "uploadStart",
    "uploadEnd",
]);
const uploadPercentage = ref(0);
const uploadStatus = ref("");
const currentUploadingFile = ref(null);
const uploadedFiles = ref([]);
const currentUploadController = ref(null);

//==============================================================================
async function handleUpload(event) {
    uploadPercentage.value = 0;
    uploadStatus.value = "uploading";
    console.log('event.file', event.file);
    
    // Emit upload start event
    emit("uploadStart");
    
    // Set current uploading file info
    currentUploadingFile.value = {
        name: event.file.name,
        original_name: event.file.name,
        size: formatFileSize(event.file.size),
        uid: event.file.uid,
    };

    console.log(
        "Starting upload for:",
        event.file.name,
        "Size:",
        event.file.size
    );

    // File size check (max 500MB)
    if (event.file.size > 500 * 1024 * 1024) {
        ElNotification({
            title: "Error",
            message: "The file size is greater than 500MB",
            type: "error",
            position: "bottom-left",
        });
        resetUploadState();
        return false;
    }

    // File type check
    const fileExtension = event.file.name.split(".").pop();
    if (!props.allowedFiles.includes(fileExtension.toLowerCase())) {
        ElNotification({
            title: "Error",
            message: "The file type is not allowed",
            type: "error",
            position: "bottom-left",
        });
        resetUploadState();
        return false;
    }

    // Prepare form data
    const formData = new FormData();
    formData.append("type", props.type);
    formData.append("file", event.file);

    // Create AbortController for cancellation
    const controller = new AbortController();
    currentUploadController.value = controller;

    // Axios config
    const config = {
        url: "/files",
        baseURL: import.meta.env.VITE_VUE_APP_ROOT_API,
        data: formData,
        method: "POST",
        withCredentials: true,
        signal: controller.signal, // Add abort signal
        headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "X-Locale": "en",
            "X-Disable-Global-Loader": "true", // Disable global loader for file uploads
        },
        onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
                // Cap progress at 95% until server responds
                let progress =
                    (progressEvent.loaded / progressEvent.total) * 95;
                uploadPercentage.value = Math.min(Math.round(progress), 95);
            }
        },
    };

    try {
        const { data } = await FileApi.upload(config);

        const uploadedFile = {
            uid: event.file.uid,
            name: data.data.file.name,
            original_name: data.data.file.original_name,
            id: data.data.file.id,
            type: data.data.file.type,
            size: formatFileSize(event.file.size),
        };

        fileList.value.push(uploadedFile);
        uploadedFiles.value.push(uploadedFile);

        // Set success state
        uploadStatus.value = "success";
        uploadPercentage.value = 100;

        // Clear ElUpload internal files so re-selecting the same file works
        clearUploadInput();

        // Reset upload state after a short delay
        setTimeout(() => {
            resetUploadState();
        }, 1500);

        emit("successUpload", data.data);
        emit("uploadEnd");
    } catch (error) {
        // Check if upload was cancelled
        if (error.name === "AbortError" || error.code === "ERR_CANCELED") {
            resetUploadState();
            return;
        }

        uploadStatus.value = "error";
        uploadPercentage.value = 0;

        // Keep file info for a bit to show error state
        currentUploadingFile.value = {
            ...currentUploadingFile.value,
            error: error.message || "Upload failed",
        };

        // Reset upload state after delay
        setTimeout(() => {
            resetUploadState();
        }, 2000);

        emit("errorUpload", error.response);
        emit("uploadEnd");
    }
}

// Helper to reset state
function resetUploadState() {
    currentUploadingFile.value = null;
    uploadStatus.value = "";
    uploadPercentage.value = 0;
    currentUploadController.value = null;
}

async function handleRemove(UploadFile) {
    let file = fileList.value.find((file) => file.uid === UploadFile.raw.uid);

    if (file) {
        // Remove from UI without API call
        removeUploadedFile(file.uid);
        emit("successDeleteUpload", file);
        // Also clear ElUpload internal list so the same file can be chosen again
        clearUploadInput();
    }
}

async function handleBeforeUpload(UploadFile, file) {
    return true;
}

const progress = () => {};

// Format file size for display
function formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

// Remove uploaded file from display
function removeUploadedFile(fileUid) {
    const index = uploadedFiles.value.findIndex((file) => file.uid === fileUid);
    if (index > -1) {
        uploadedFiles.value.splice(index, 1);
    }
}

// Cancel current upload
function cancelUpload() {
    // Abort the current upload if it's in progress
    if (currentUploadController.value) {
        currentUploadController.value.abort();
        console.log("Upload cancelled");
    }

    // Reset UI state
    currentUploadingFile.value = null;
    uploadStatus.value = "";
    uploadPercentage.value = 0;
    currentUploadController.value = null;

    // Ensure the input is cleared so the same file can be picked again
    clearUploadInput();
}

// Remove file from uploaded list
function removeFile(fileUid) {
    const file = fileList.value.find((file) => file.uid === fileUid);

    if (file) {
        // Remove from UI without API call
        removeUploadedFile(fileUid);
        // Also remove from fileList for Element Plus
        const index = fileList.value.findIndex((file) => file.uid === fileUid);
        if (index > -1) {
            fileList.value.splice(index, 1);
        }
        // Emit success event to parent component
        emit("successDeleteUpload", file);
        // Clear ElUpload internal list so the same file can be chosen again
        clearUploadInput();
    }
}

// Clear ElUpload internal file list to allow re-selecting the same file
function clearUploadInput() {
    if (uploadRef.value && typeof uploadRef.value.clearFiles === "function") {
        uploadRef.value.clearFiles();
    }
}
// computed: dynamically access model[name]
const modelField = computed(() => props.model?.[props.name]);

watch(
    modelField,
    (newValue) => {
        if (!newValue || !Array.isArray(newValue)) {
            uploadedFiles.value = [];
            fileList.value = [];
            return;
        }

        // Map only if the file data comes from backend
        uploadedFiles.value = newValue.map((file) => ({
            uid:
                file.uid ||
                file.id ||
                Math.random().toString(36).substring(2, 9),
            name:
                file.name ||
                file.original_name ||
                file.file_name ||
                `File-${file.id}`,
            size: file.size || "N/A",
            type: file.type || "",
            original_name: file.original_name || file.name,
            id: file.id,
        }));

        fileList.value = [...uploadedFiles.value];
    },
    { immediate: true, deep: true }
);
</script>

<template>
    <div class="file-upload-container">
        <!-- Upload Area -->
        <el-upload
            class="upload-demo"
            ref="uploadRef"
            :http-request="handleUpload"
            :multiple="multiple"
            :limit="limit"
            :before-upload="handleBeforeUpload"
            :on-remove="handleRemove"
            :accept="props.allowedFiles"
            drag
            :show-file-list="false"
            :auto-upload="true"
            :disabled="!multiple && uploadedFiles.length >= 1"
        >
            <div class="upload-area" style="min-height: 240px">
                <div class="upload-icon">
                    <svg
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                            stroke="#9CA3AF"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M7 10L12 5L17 10"
                            stroke="#9CA3AF"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M12 5V15"
                            stroke="#9CA3AF"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </div>
                <div class="upload-text">
                    <span class="click-text">Click to upload</span>
                    <span class="drag-text">or drag and drop</span>
                </div>
            </div>
        </el-upload>

        <!-- Upload Progress Card -->
        <div
            v-if="
                currentUploadingFile &&
                (uploadStatus === 'uploading' || uploadStatus === 'success')
            "
            class="upload-progress-card"
        >
            <div class="file-info">
                <div class="file-icon">
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
                            fill="#8B5CF6"
                            stroke="#8B5CF6"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M14 2V8H20"
                            stroke="#8B5CF6"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </div>
                <div class="file-details">
                    <div class="file-name">{{ currentUploadingFile.name }}</div>
                    <div class="file-size">{{ currentUploadingFile.size }}</div>
                </div>
                <div class="file-actions">
                    <div class="progress-percentage">
                        {{ uploadPercentage }}%
                        <span
                            v-if="uploadStatus === 'success'"
                            class="status-indicator success"
                            >✓</span
                        >
                        <span
                            v-else-if="uploadStatus === 'error'"
                            class="status-indicator error"
                            >✗</span
                        >
                        <span v-else class="status-indicator uploading">⟳</span>
                    </div>
                    <button class="delete-btn" @click="cancelUpload">
                        <svg
                            width="18"
                            height="20"
                            viewBox="0 0 18 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M12.3333 4.99984V4.33317C12.3333 3.39975 12.3333 2.93304 12.1517 2.57652C11.9919 2.26292 11.7369 2.00795 11.4233 1.84816C11.0668 1.6665 10.6001 1.6665 9.66667 1.6665H8.33333C7.39991 1.6665 6.9332 1.6665 6.57668 1.84816C6.26308 2.00795 6.00811 2.26292 5.84832 2.57652C5.66667 2.93304 5.66667 3.39975 5.66667 4.33317V4.99984M7.33333 9.58317V13.7498M10.6667 9.58317V13.7498M1.5 4.99984H16.5M14.8333 4.99984V14.3332C14.8333 15.7333 14.8333 16.4334 14.5608 16.9681C14.3212 17.4386 13.9387 17.821 13.4683 18.0607C12.9335 18.3332 12.2335 18.3332 10.8333 18.3332H7.16667C5.76654 18.3332 5.06647 18.3332 4.53169 18.0607C4.06129 17.821 3.67883 17.4386 3.43915 16.9681C3.16667 16.4334 3.16667 15.7333 3.16667 14.3332V4.99984"
                                stroke="#96959D"
                                stroke-width="1.66667"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </button>
                </div>
            </div>
            <div class="progress-bar-container">
                <div class="progress-bar">
                    <div
                        class="progress-fill"
                        :class="{
                            'progress-success': uploadStatus === 'success',
                            'progress-error': uploadStatus === 'error',
                        }"
                        :style="{ width: uploadPercentage + '%' }"
                    ></div>
                </div>
            </div>
        </div>

        <!-- Uploaded Files List -->
        <div v-if="uploadedFiles.length > 0" class="uploaded-files">
            <div
                v-for="file in uploadedFiles"
                :key="file.uid"
                class="uploaded-file-item"
            >
                <div class="file-info">
                    <div class="file-icon">
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
                                fill="#10B981"
                                stroke="#10B981"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                            <path
                                d="M14 2V8H20"
                                stroke="#10B981"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </div>
                    <div class="file-details">
                       
                        <div class="file-name">{{  file.original_name ?? file.name }}</div>
                        <div class="file-size">{{ file.size }}</div>
                    </div>
                </div>
                <button
                    class="delete-btn"
                    style="margin-bottom: auto"
                    @click="removeFile(file.uid)"
                >
                    <svg
                        width="18"
                        height="20"
                        viewBox="0 0 18 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M12.3333 4.99984V4.33317C12.3333 3.39975 12.3333 2.93304 12.1517 2.57652C11.9919 2.26292 11.7369 2.00795 11.4233 1.84816C11.0668 1.6665 10.6001 1.6665 9.66667 1.6665H8.33333C7.39991 1.6665 6.9332 1.6665 6.57668 1.84816C6.26308 2.00795 6.00811 2.26292 5.84832 2.57652C5.66667 2.93304 5.66667 3.39975 5.66667 4.33317V4.99984M7.33333 9.58317V13.7498M10.6667 9.58317V13.7498M1.5 4.99984H16.5M14.8333 4.99984V14.3332C14.8333 15.7333 14.8333 16.4334 14.5608 16.9681C14.3212 17.4386 13.9387 17.821 13.4683 18.0607C12.9335 18.3332 12.2335 18.3332 10.8333 18.3332H7.16667C5.76654 18.3332 5.06647 18.3332 4.53169 18.0607C4.06129 17.821 3.67883 17.4386 3.43915 16.9681C3.16667 16.4334 3.16667 15.7333 3.16667 14.3332V4.99984"
                            stroke="#96959D"
                            stroke-width="1.66667"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </button>
            </div>
        </div>

        <!-- Upload Tips -->
        <div class="upload-tips" v-if="false">
            <div v-if="props.allowedFiles" class="allowed-files">
                Allowed Files: {{ props.allowedFiles }}
            </div>
            <div class="file-size-limit">
                The maximum file size allowed is 500MB.
            </div>
        </div>
    </div>
</template>
